import { ActivityContext } from '@/contexts/ActivityContext';
import { LocationContext } from '@/contexts/LocationContext';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useContext, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import LocationCard from './LocationCard';
import { Location } from '@/api/types/Location';
import ManageLocationsDialog from './ManageLocationsDialog';
import { Edit } from '@mui/icons-material';

type LocationsDialogProps = {
  open: boolean;
  onClose: () => void;
};

const LocationsDialog = ({ open, onClose }: LocationsDialogProps) => {
  const { t } = useTranslation();
  const { locations } = useContext(LocationContext);
  const { activity, setActivity } = useContext(ActivityContext);

  const [manageLocationDialog, setManageLocationDialog] = useState(false);
  const [sortedLocations, setSortedLocations] = useState<Location[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<Location | undefined>(undefined);

  const locationRefs = useRef<(HTMLDivElement | null)[]>([]);
  const firstCardRef = useRef<HTMLDivElement | null>(null);
  const addButtonRef = useRef<HTMLButtonElement | null>(null);

  const handleSelect = (selectedLocation: Location) => {
    if (activity) {
      setActivity({ ...activity, location: selectedLocation });
      onClose();
    } else {
      setManageLocationDialog(true);
      setSelectedLocation(selectedLocation);
    }
  };

  const handleEditLocation = (location: Location) => {
    setSelectedLocation(location);
    setManageLocationDialog(true);
  };

  const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (index < sortedLocations.length - 1) {
          locationRefs.current[index + 1]?.focus();
        } else {
          addButtonRef.current?.focus();
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (index > 0) {
          locationRefs.current[index - 1]?.focus();
        }
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (open && sortedLocations.length > 0) {
      const timer = setTimeout(() => {
        if (firstCardRef.current) {
          const focusableElement = firstCardRef.current.querySelector('button, [tabindex="0"]');
          if (focusableElement instanceof HTMLElement) {
            focusableElement.focus();
          }
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open, sortedLocations.length]);

  useEffect(() => {
    locationRefs.current = locationRefs.current.slice(0, sortedLocations.length);
  }, [sortedLocations]);

  useEffect(() => {
    const filteredAndSorted = [...locations].sort((a, b) => {
      if (a.color && b.color && a.color < b.color) {
        return -1;
      }
      if (a.color && b.color && a.color > b.color) {
        return 1;
      }
      return 0;
    });
    setSortedLocations(filteredAndSorted);
  }, [locations]);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        aria-labelledby="locations-dialog-title"
        aria-describedby="locations-dialog-description"
        fullWidth={true}
        maxWidth={'md'}
      >
        <DialogTitle id="locations-dialog-title">{t('My locations')}</DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: 'column',
          }}
        >
          <Stack
            spacing={2}
            direction="column"
            role="list"
            aria-label={t('Location list') as string}
          >
            {sortedLocations.length === 0 ? (
              <Typography>{t('No locations found')}</Typography>
            ) : (
              sortedLocations.map((location, index) => (
                <div
                  key={location.locationId}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  role="listitem"
                >
                  <LocationCard
                    ref={(el) => {
                      locationRefs.current[index] = el;
                      if (index === 0) firstCardRef.current = el;
                    }}
                    location={location}
                    icon={<Edit />}
                    onSelect={() => handleSelect(location)}
                    onIconClick={() => handleEditLocation(location)}
                    isClickable={true}
                    ariaLabel={t('Edit') as string}
                  />
                </div>
              ))
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            ref={addButtonRef}
            onClick={() => {
              setSelectedLocation(undefined);
              setManageLocationDialog(true);
            }}
            variant="contained"
            color="primary"
            sx={{ mr: 1 }}
          >
            {t('Add location')}
          </Button>
          <Button onClick={onClose} variant="contained" color="primary">
            {t('Close')}
          </Button>
        </DialogActions>
      </Dialog>
      <ManageLocationsDialog
        open={manageLocationDialog}
        onClose={() => setManageLocationDialog(false)}
        location={selectedLocation}
      />
    </>
  );
};

export default LocationsDialog;
